import type { CheckPermParams, LoginParams, RequestParams } from '@sydle/ui-api';
import { OneApi } from '../../components/one-api/one-api';
import type { AuthenticatorsConfig } from '../../models/OrganizationConfigs';
import type { UserData } from '../../models/UserData';

import { createMutationEndpoint, createQueryEndpoint, TagHelpers } from '../requests/helpers';
import { logout, setUser } from './user.slice';

export type LogoutParams = { authenticator: AuthenticatorsConfig; onlyClient?: boolean };

export type LogoutResponse = { didExternalRedirect?: boolean };

export type SendPasswordRecoveryEmailParams = {
  organization: string;
  login: string;
  requestParams?: Partial<RequestParams>;
};

export type ChangePasswordParams = {
  token: string;
  password: string;
  requestParams?: Partial<RequestParams>;
};

export const UserEndpoints = {
  login: createMutationEndpoint<LoginParams, UserData>('UserApi.login', {
    async queryFn(params: LoginParams, { dispatch }) {
      return await OneApi.userAPI.login(params).then((response) => {
        if (response.data?.operationCode) {
          return response;
        }
        dispatch(setUser(response.data as UserData));
        return response;
      });
    },
    invalidatesTags: TagHelpers.invalidatesUnauthorized,
  }),

  secondAuthLogin: createMutationEndpoint<LoginParams, UserData>('UserApi.secondAuthLogin', {
    async queryFn(params: LoginParams, { dispatch }) {
      return await (OneApi.userAPI as any).secondAuthLogin(params).then((response) => {
        if (response.data?.operationCode) {
          return response;
        }
        dispatch(setUser(response.data as UserData));
        return response;
      });
    },
    invalidatesTags: TagHelpers.invalidatesUnauthorized,
  }),

  changePassword: createMutationEndpoint<ChangePasswordParams, any>('UserApi.changePassword', {
    async queryFn(params: ChangePasswordParams) {
      // ToDo: Utilizar OneApi.userAPI.changePassword quando lançar a issue sydle-ui-core#160
      const data = {
        token: params.token,
        password: btoa(params.password),
      };

      const requestParams = {
        url: 'passwordRecovery/changePassword',
        body: data,
      };
      return OneApi.restService.post(requestParams);
    },
    invalidatesTags: TagHelpers.invalidatesUnauthorized,
  }),

  sendPasswordRecoveryEmail: createMutationEndpoint<SendPasswordRecoveryEmailParams, string>(
    'UserApi.sendPasswordRecoveryEmail',
    {
      async queryFn(params: SendPasswordRecoveryEmailParams) {
        return await OneApi.userAPI.sendPasswordRecoveryEmail(params as any);
      },
      invalidatesTags: TagHelpers.invalidatesUnauthorized,
    },
  ),

  logout: createMutationEndpoint<LogoutParams, LogoutResponse>('UserApi.logout', {
    async queryFn({ authenticator, onlyClient }: LogoutParams, { dispatch }) {
      if (onlyClient) {
        OneApi.userAPI.logoutClient();
        return { data: {} };
      }

      let logoutResponse;
      const user = OneApi.userAPI.getUser();

      if (user && authenticator?.getLogoutUrl?.length) {
        const header = {
          Authorization: `Bearer ${user.accessToken?.token}`,
        };
        logoutResponse = await OneApi.restService.post({
          addBaseUrl: false,
          url: authenticator.getLogoutUrl,
          headers: user.accessToken?.token && header,
          addUserTokenOnHeader: true,
        });
      }

      const result = await OneApi.userAPI.logout();
      if (result.error) {
        console.error('Logout error: ', result.error);
      }
      dispatch(logout());

      if (logoutResponse?.data) {
        window.location.href = logoutResponse.data.url;

        return { data: { didExternalRedirect: true } };
      }

      return { data: {} };
    },
  }),

  loadLoggedUser: createQueryEndpoint<void, UserData>('UserApi.loadLoggedUser', {
    async queryFn(_args, { dispatch }) {
      const user = OneApi.userAPI.getUser();
      if (user) {
        dispatch(setUser(user));
      }
      return { data: user };
    },
  }),

  checkPerm: createQueryEndpoint<CheckPermParams, boolean>('UserApi.checkPerm', {
    queryFn: async (params) => {
      return await OneApi.userAPI.checkPerm(params);
    },
  }),
};
